/**
 * VRS Language Switcher Component
 *
 * A compact language toggle for switching between English and Arabic.
 * Designed to blend seamlessly with the Jitsi toolbar.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { connect } from 'react-redux';
import { makeStyles } from 'tss-react/mui';
import { translate } from '../../base/i18n/functions';
import i18next from '../../base/i18n/i18next';
import { resolveUiLanguage, UI_LANGUAGES } from '../../base/i18n/uiLanguage';
import { setPersistentItem } from '../../vrs-auth/storage';
// Each language is labelled with its own endonym and its ISO code. Deliberately
// no flags: Arabic is not one country's language, and a Gulf-facing product
// showing a single national flag for it reads as excluding everyone else.
const LANGUAGES = {
    en: { name: 'English', dir: 'ltr' },
    ar: { name: 'العربية', dir: 'rtl' }
};
const useStyles = makeStyles()(theme => ({
    container: {
        position: 'relative',
        display: 'inline-block'
    },
    // Compact button style that blends with Jitsi toolbar
    button: {
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
        padding: '6px 10px',
        backgroundColor: 'rgba(255, 255, 255, 0.1)',
        border: '1px solid rgba(255, 255, 255, 0.2)',
        borderRadius: '6px',
        color: 'white',
        fontSize: '13px',
        fontWeight: 500,
        cursor: 'pointer',
        transition: 'all 0.15s ease',
        '&:hover': {
            backgroundColor: 'rgba(255, 107, 53, 0.25)',
            borderColor: 'rgba(255, 107, 53, 0.4)'
        },
        '&:active': {
            backgroundColor: 'rgba(255, 107, 53, 0.35)',
        }
    },
    buttonDark: {
        backgroundColor: 'rgba(0, 0, 0, 0.3)',
        border: '1px solid rgba(255, 255, 255, 0.15)',
        '&:hover': {
            backgroundColor: 'rgba(255, 107, 53, 0.25)',
            borderColor: 'rgba(255, 107, 53, 0.4)'
        }
    },
    languageCode: {
        fontSize: '12px',
        fontWeight: 600,
        textTransform: 'uppercase',
        opacity: 0.9,
        minWidth: '24px',
        textAlign: 'center'
    },
    // Dropdown menu
    menu: {
        position: 'absolute',
        top: 'calc(100% + 6px)',
        insetInlineEnd: 0,
        backgroundColor: '#1a1a1a',
        border: '1px solid rgba(255, 255, 255, 0.15)',
        borderRadius: '8px',
        padding: '6px',
        minWidth: '150px',
        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.4)',
        zIndex: 10000
    },
    menuItem: {
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        padding: '10px 12px',
        color: 'white',
        cursor: 'pointer',
        transition: 'background 0.15s',
        borderRadius: '6px',
        '&:hover': {
            backgroundColor: 'rgba(255, 107, 53, 0.2)'
        }
    },
    menuItemSelected: {
        backgroundColor: 'rgba(255, 107, 53, 0.15) !important',
    },
    languageName: {
        flex: 1,
        fontSize: '14px'
    },
    checkmark: {
        color: '#28a745',
        fontSize: '14px'
    },
    // RTL support
    rtl: {
        direction: 'rtl'
    }
}));
/**
 * Language switcher component for toggling between English and Arabic.
 * Compact design that blends with the Jitsi toolbar.
 */
const LanguageSwitcher = ({ _language, t }) => {
    const { classes, cx } = useStyles();
    const [isOpen, setIsOpen] = useState(false);
    const [currentLang, setCurrentLang] = useState(_language || 'en');
    const [useDarkStyle, setUseDarkStyle] = useState(true);
    // Detect if we're in a toolbar (check parent elements)
    useEffect(() => {
        const checkToolbarParent = () => {
            const toolbar = document.querySelector('.toolbox-content-wrapped, .toolbox, [data-testid="toolbar"]');
            setUseDarkStyle(!toolbar); // Use dark style if not in toolbar (default)
        };
        checkToolbarParent();
        // Listen for DOM changes
        const observer = new MutationObserver(checkToolbarParent);
        observer.observe(document.body, { childList: true, subtree: true });
        return () => observer.disconnect();
    }, []);
    // Sync with Redux language changes
    useEffect(() => {
        const lang = (_language || 'en').substring(0, 2);
        if (lang === 'en' || lang === 'ar') {
            setCurrentLang(lang);
        }
    }, [_language]);
    // Close menu when clicking outside
    useEffect(() => {
        const handleClickOutside = (e) => {
            const target = e.target;
            if (!target.closest?.('.vrs-language-switcher')) {
                setIsOpen(false);
            }
        };
        if (isOpen) {
            document.addEventListener('click', handleClickOutside, { capture: true });
            return () => document.removeEventListener('click', handleClickOutside, { capture: true });
        }
    }, [isOpen]);
    // Handle keyboard (ESC to close)
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key === 'Escape' && isOpen) {
                setIsOpen(false);
            }
        };
        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [isOpen]);
    const handleLanguageChange = useCallback((langCode) => {
        if (langCode === currentLang) {
            setIsOpen(false);
            return;
        }
        setCurrentLang(langCode);
        setIsOpen(false);
        // i18next's browser detector caches to the `language` localStorage key,
        // which is what it reads back on the next load. `vrs_preferred_language`
        // is the separate key the VRS/VCS waiting-room screens read, so both are
        // written. Document direction is applied by the shared `languageChanged`
        // handler in base/i18n/i18next.ts.
        setPersistentItem('vrs_preferred_language', resolveUiLanguage(langCode));
        void i18next.changeLanguage(resolveUiLanguage(langCode)).then(() => {
            // Reload so memoised strings captured at mount are rebuilt in the
            // new language. The language is persisted above, so the reload
            // comes back in the language just chosen.
            window.location.reload();
        });
    }, [currentLang]);
    // The English/Arabic quick switch shows only where the interface can be
    // Arabic: the MalkaVCS tenant (base/i18n/uiLanguage.ts).
    if (!UI_LANGUAGES.includes('ar')) {
        return null;
    }
    const currentConfig = LANGUAGES[currentLang];
    const otherLang = currentLang === 'en' ? 'ar' : 'en';
    return (React.createElement("div", { className: cx('vrs-language-switcher', classes.container) },
        React.createElement("button", { className: cx(classes.button, useDarkStyle && classes.buttonDark), onClick: (e) => {
                e.stopPropagation();
                setIsOpen(!isOpen);
            }, type: "button", "aria-label": t('vrs.switchLanguage'), "aria-expanded": isOpen, "aria-haspopup": "menu", title: t('vrs.switchLanguageTo', { language: LANGUAGES[otherLang].name }) },
            React.createElement("span", { className: classes.languageName }, currentConfig.name),
            React.createElement("span", { className: classes.languageCode }, currentLang.toUpperCase())),
        isOpen && (React.createElement("div", { className: classes.menu, role: "menu", onClick: (e) => e.stopPropagation() }, Object.entries(LANGUAGES).map(([code, config]) => (React.createElement("div", { key: code, className: cx(classes.menuItem, currentLang === code && classes.menuItemSelected), onClick: () => handleLanguageChange(code), role: "menuitemradio", "aria-checked": currentLang === code, lang: code, dir: config.dir, tabIndex: 0, onKeyPress: (e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                    handleLanguageChange(code);
                }
            } },
            React.createElement("span", { className: classes.languageName }, config.name),
            currentLang === code && (React.createElement("span", { className: classes.checkmark }, "\u2713")))))))));
};
function _mapStateToProps(state) {
    return {
        _language: state['features/base/i18n']?.currentLanguage || i18next.language || 'en'
    };
}
export default translate(connect(_mapStateToProps)(LanguageSwitcher));
